import { Http } from '@/services/http'
import { Injectable } from '@tanbo/vue-di-plugin'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import {
  AllServiceRes,
  flatAllService,
  PriceList,
  ServiceCommonService
} from '@/views/dashboard/service/service-common.service'

export const CYCLE_DESC = {
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
}

export interface CalcPriceReq {
  /** 价格id集合 */
  price_ids: string[]
  /** 购买终端数量 */
  purchase_count: number
  /** 订单类型 open-开通;; continue-延长有效期 */
  action: string
  /** 续费的订单明细id */
  old_product_id?: string
}

export interface PriceCalculationList {
  /** 计算详情 */
  cal_detail: string
  /** 计算介绍 */
  cal_desc: string
}

export interface CalculateDate {
  /** 终端数量 */
  count?: number
  /** 到期时间（秒） */
  expire_time?: number
}

export interface CalcPriceRes {
  /** 活动描述 */
  activity_desc: string
  /** 原价, 单位分 */
  origin_price: number
  /** 实际价格, 单位分 */
  current_price: number
  /** 价格计算列表 */
  price_calculation_list: PriceCalculationList[]
  package_price_calculation_list: PriceCalculationList[]
  /** 价格计算公式 */
  price_calculation_formula: string
  /** 价格计算过程 */
  price_calculation_formula_desc: string
  after_calculate_date?: CalculateDate
  before_calculate_date?: CalculateDate
}

export interface CreateOrderReq {
  price_ids?: number[] // 套餐id, 计算价格和下单使用
  action?: string // 订单类型 open-开通; upgrade-升级; expansion-扩充终端; continue-延长有效期
  purchase_count?: number // 购买终端数量 只有在开通和扩充终端时才需要传入此字段
  /** 支付方式 1-在线支付 2-公对公转账 */
  pay_type?: number
  /** 是否取消未支付订单 1-是 0-否 */
  is_cancel?: number
}

export interface SubscribeDetailsRes {
  /** 订单详情 */
  price_list: PriceList[]
  /** 公司名 */
  company_name: string
  /** 购买数量 */
  purchase_count: number
  cycle_count: number
}

/** 前端字段 */
export interface CycleServiceItem {
  cycle_count: number
  price_list: PriceList[]
}

export interface CreateOrderRes {
  product_id: string // 产品id，支付或商品详情接口使用
  err_code: number // 错误码
  err_msg: string// 错误信息
  is_cancel: number
}

export interface SubscribeDetailsReq {
  product_id: string,
}

@Injectable()
export class SubscribeDetailsService {
  constructor (private http: Http, private serviceCommon: ServiceCommonService) {}

  @InvokeThrottle()
  calcPrice (data: CalcPriceReq) {
    return this.http.post<CalcPriceRes>('/permission_order/calc_price', data)
  }

  @InvokeThrottle()
  createOrder (data: CreateOrderReq) {
    return this.http.post<CreateOrderRes>('/permission_order/create', data)
  }

  getServiceGroupByUnionIds (unionIds: number[]) {
    return this.serviceCommon.getServiceGroup().then(res => {
      return res.price_list.filter(v => unionIds.includes(v.display_union_id))
    })
  }

  getServiceByUnionIds (unionIds: number[]) {
    return this.serviceCommon.getAllService().then(res => {
      return flatAllService(res.prices).filter(v => unionIds.includes(v.display_union_id))
    })
  }

  getCycleServiceMergeByUnionIds (groupUnionIds: number[], serviceUnionIds: number[]) {
    return Promise.all([this.getServiceGroupByUnionIds(groupUnionIds), this.getServiceByUnionIds(serviceUnionIds)]).then(res => {
      return formatCycleData(res[0].concat(res[1]))
    })
  }

  getSubscribeDetails (data: SubscribeDetailsReq) {
    return this.http.post<SubscribeDetailsRes>('/permission_order/order_detail', data)
  }
}

export function formatCycleData (priceList: PriceList[]) {
  let allCycleCount = priceList.map(v => v.cycle_count).filter(v => CYCLE_DESC[v]).sort((a, b) => a - b)
  allCycleCount = Array.from(new Set(allCycleCount))
  return allCycleCount.map(count => ({
    cycle_count: count,
    price_list: priceList.filter(v => v.cycle_count === count)
  }))
}
